import { messageCallback, PresenceMessage, Realtime, RealtimeChannel } from 'ably';

import store from 'pages/store';
import {
  updateAblyConnectionStatus,
  updateBrowserCFDConnectionStatus,
  updateDevices,
  updateHubConnectionStatus,
} from 'pages/store/reducers/hub';
import { getCFDAppUrl } from 'pages/views/devices/utils';
import { getCFDEnabled } from 'pages/views/devices/utils/get-cfd-enabled';
import Logger from 'pages/views/devices/utils/logger';
import { OPTIONS } from 'pages/views/devices/utils/options';
import {
  getChannelName,
  getHubConnectedOnce,
  getHubEnabled,
  removeHubSmartConnection,
  setHubConnectedOnce,
} from 'pages/views/devices/utils/storage';
import { hasFeature } from 'root/api/admin/features';

import { onVisibilityChange } from '../utils/focus-handler';
import { SocketBase } from './socketBase';
import {
  AblyStatus,
  AbstractSocket,
  CFD_MESSAGES,
  HUB_MESSAGES,
  isStatusConnected,
  isStatusDisconnected,
  MEMBER_TYPE,
  PRINTER_MESSAGES,
  RetailMessages,
  Transport,
} from './types';
import { getLoggerKeyFromEvent } from './utils';

export class AblyClient extends SocketBase implements AbstractSocket {
  public status: AblyStatus = 'Disconnected';

  protected transport: Transport = 'Ably';

  private channelName: string = '';

  private static readonly LSPAY_CHANNEL_PREFIX = 'lspay:';

  private static readonly LSPAY_CFD_CLIENT_ID = 'r-series';

  private client: Realtime | undefined;

  private channel: RealtimeChannel | undefined;

  constructor() {
    super();

    window.addEventListener('load', () => {
      const updateOnlineStatus = () => {
        if (navigator.onLine) {
          this.connect();
        } else if (!navigator.onLine) {
          this.disconnect();
        }
      };

      window.addEventListener('online', updateOnlineStatus);
      window.addEventListener('offline', updateOnlineStatus);
    });

    onVisibilityChange((isVisible: boolean) => {
      if (!isStatusDisconnected(this.status) || !getHubEnabled()) return;
      const formNamesBlockList = ['close_register'];
      const isDenyListed = formNamesBlockList.some((formName) => {
        return window.location.href.includes(formName);
      });
      // FIXME LSR-13963: This code isn't running and needs to be investigated
      if (isStatusConnected(this.status)) {
        if (isVisible && window.location.pathname === '/register.php' && !isDenyListed) {
          this.send(CFD_MESSAGES.SHOW_SALE);
        } else {
          this.send(CFD_MESSAGES.HIDE_SALE);
        }
      } else if (isVisible && isStatusDisconnected(this.status)) {
        this.connect();
      }
    });
  }

  public send = async (event: RetailMessages, data?: any) => {
    if (isStatusConnected(this.status)) {
      if (event === CFD_MESSAGES.HIDE_SALE && this.isSaleHidden) return;

      this.isSaleHidden = [CFD_MESSAGES.HIDE_SALE, CFD_MESSAGES.COMPLETE_SALE].includes(
        event as CFD_MESSAGES
      );

      if (
        event === HUB_MESSAGES.UPDATE_CONFIG &&
        typeof data?.config?.[OPTIONS.IS_DEBUG_MODE] === 'boolean'
      ) {
        /**
         * DefaultRealtime is exported as Realtime
         * https://github.com/ably/ably-js/blob/2.12.0/src/platform/nativescript/index.ts#L53
         * DefaultRealtime extends BaseRealtime
         * https://github.com/ably/ably-js/blob/2.12.0/src/common/lib/client/defaultrealtime.ts#L28
         * BaseRealtime extends BaseClient
         * https://github.com/ably/ably-js/blob/2.12.0/src/common/lib/client/baserealtime.ts#L21
         * BaseClient exposes setLog
         * https://github.com/ably/ably-js/blob/2.12.0/src/common/lib/client/baseclient.ts#L174-L17
         */
        // @ts-ignore Realtime type does not have setLog, but it is inherited from BaseClient
        this.client?.setLog?.({ level: this.getLogLevel() });
      }

      if (CFD_MESSAGES[event as keyof typeof CFD_MESSAGES] && !getCFDEnabled()) return;

      this.channel?.publish(event, data);
      const loggerKey = getLoggerKeyFromEvent(event);
      if (await hasFeature('RegisterCfdChannelLogging')) {
        if (loggerKey === 'CFD') {
          const accountId = window.merchantos.session.getSystemCustomerID();
          const registerId = window.merchantos.session.getRegister()?.registerID;

          Logger.log(`CFD Message: ${event}`, {
            accountId,
            registerId,
            channelName: this.channelName,
          });
        }
      }
      Logger.debug(`Ably:${loggerKey}: ${event}`, data);
    } else {
      this.queueMessage(event, data);
    }
  };

  public connect() {
    if (
      document.visibilityState !== 'visible' ||
      !getHubEnabled() ||
      ['connected', 'connecting'].includes(this.client?.connection?.state as string)
    ) {
      return;
    }

    this.channelName = getChannelName();
    Logger.debug(`Hub: channelName ${this.channelName}`);
    if (this.client) {
      this.client.connection.connect();
    } else {
      const authUrl = this.getAblyAuthUrl();
      this.client = new Realtime({
        clientId: this.isLsPayCfd() ? AblyClient.LSPAY_CFD_CLIENT_ID : MEMBER_TYPE.RETAIL,
        authUrl,
        logLevel: this.getLogLevel(),
        logHandler: Logger.debug,
      });

      this.client.connection.once('connected', () => {
        const ablyChannelName = this.isLsPayCfd()
          ? this.channelName
          : `private:${this.channelName}`;
        this.channel = this.client!.channels.get(ablyChannelName);
        this.channel.on('failed', this.onChannelFailed);
        this.channel.subscribe(HUB_MESSAGES.GET_CONFIG, this.onGetConfig);
        this.channel.subscribe(HUB_MESSAGES.DEVICES, this.onDevices);
        this.channel.presence.subscribe('enter', this.onPresenceMemberAdded);
        this.channel.presence.subscribe('leave', this.onPresenceMemberLeave);
        this.channel.subscribe(PRINTER_MESSAGES.PRINT_COMPLETED, this.onPrintCompleted);
        this.channel.subscribe(PRINTER_MESSAGES.PRINT_ERROR, this.onPrintError);
        this.channel.subscribe(CFD_MESSAGES.TIP_CONFIRMED, this.onTipSelected);
        this.channel.subscribe(CFD_MESSAGES.CUSTOMER_SAVE, this.onCustomerSave);
        this.channel.subscribe(CFD_MESSAGES.CUSTOMER_CANCEL, this.onCustomerCancel);
      });

      this.client.connection.on('connected', () => {
        this.setAblyStatus('Connected');
        this.checkOnLastConnection();
        this.channel!.presence.get()
          .then(this.onPresenceMemberGet)
          .catch((err) => {
            if (err) Logger.error(err);
          });
        this.channel!.presence.enter();
      });

      this.client.connection.on('closed', () => this.setAblyStatus('Disconnected'));
      this.client.connection.on('failed', () => this.setAblyStatus('Error'));
      this.client.connection.on('suspended', () => this.setAblyStatus('Error'));
      this.client.connection.on('disconnected', () => this.setAblyStatus('Disconnected'));
    }

    setTimeout(removeHubSmartConnection, 1000);
    window.addEventListener('beforeunload', this.setUnloading);
  }

  public isBrowserCFDConnected = (): Promise<boolean> =>
    new Promise((resolve) => {
      if (this.channel) {
        this.channel.presence
          .get()
          .then((members: PresenceMessage[]) => {
            const isConnected = members?.some(({ clientId }) => clientId === MEMBER_TYPE.CFD);
            return resolve(isConnected);
          })
          .catch((err) => {
            if (err) Logger.error(err);
          });
      } else {
        resolve(false);
      }
    });

  private onPresenceMemberAdded: messageCallback<PresenceMessage> = ({
    clientId,
    connectionId,
  }) => {
    if (this.client?.connection.id !== connectionId && clientId === MEMBER_TYPE.RETAIL) {
      this.disconnect();
    } else {
      store.dispatch(updateAblyConnectionStatus('Connected'));
      this.setPreviousSuccessfulConnection();
      if (!getHubConnectedOnce()) {
        setHubConnectedOnce();
      }

      if (clientId === MEMBER_TYPE.HUB) {
        store.dispatch(updateHubConnectionStatus('Connected'));
        this.sendMessages('hub');
      } else if (clientId === MEMBER_TYPE.CFD) {
        store.dispatch(updateBrowserCFDConnectionStatus('Connected'));
        this.sendMessages('cfd');
      }
    }
  };

  private onPresenceMemberLeave = () => {
    this.channel?.presence
      .get()
      .then(this.onPresenceMemberGet)
      .catch((err) => {
        if (err) Logger.error(err);
      });
  };

  private onPresenceMemberGet = (members: PresenceMessage[]) => {
    const isHubConnected = members.some(({ clientId }) => clientId === MEMBER_TYPE.HUB);
    const isCFDConnected = members.some(({ clientId }) => clientId === MEMBER_TYPE.CFD);

    if (isHubConnected || isCFDConnected) {
      this.setPreviousSuccessfulConnection();
    }

    if (!isHubConnected && !isCFDConnected) {
      store.dispatch(updateAblyConnectionStatus('Ready'));
    } else {
      store.dispatch(updateHubConnectionStatus(isHubConnected ? 'Connected' : 'Disconnected'));

      // @NOTE The browser CFD close event for removing the selectedDisplay is not being received
      // correctly when the local socket client is the main transport, this will enforce the removal
      // of the selected display if there was previously a browser CFD connected
      if (store.getState().hub.connection.browserCFD === 'Connected' && !isCFDConnected) {
        store.dispatch(updateDevices({ selectedDisplay: {} }));
      }

      store.dispatch(
        updateBrowserCFDConnectionStatus(isCFDConnected ? 'Connected' : 'Disconnected')
      );
    }
    if (isHubConnected) {
      this.sendMessages('hub');
    }
    if (isCFDConnected) {
      this.sendMessages('cfd');
    }
  };

  private isLsPayCfd(): boolean {
    return this.channelName.startsWith(AblyClient.LSPAY_CHANNEL_PREFIX);
  }

  private setAblyStatus(status: AblyStatus) {
    this.status = status;

    if (!this.isUnLoading && store.getState().hub.connection.ably !== status) {
      store.dispatch(updateAblyConnectionStatus(status));
    }
  }

  private getAblyAuthUrl(): string {
    if (this.isLsPayCfd()) {
      return '/system/realtime/ably/token';
    }
    return `${getCFDAppUrl('hub-backend')}/api/socket/auth`;
  }

  public disconnect = () => {
    if (!['Connected', 'Ready'].includes(this.status)) return;

    this.send(HUB_MESSAGES.DISCONNECT);
    window.removeEventListener('beforeunload', this.setUnloading);

    // Make sure the disconnect event gets sent before the members gets updated
    setTimeout(async () => {
      try {
        this.client?.close();
        this.setAblyStatus('Disconnected');
        await this.channel?.presence.leave();
      } catch (err) {
        if (err) Logger.error(err);
      }
    }, 250);
  };

  private onChannelFailed = () => {
    this.setAblyStatus('Error');
  };
}
